// What the browser is holding when an auth email lands back on the origin.
//
// FIELD DEFECT, 2026-09-05 (John, iPhone). He requested the #/admin sign-in link
// in Safari, tapped it in Apple Mail, and Mail opened it in its in-app
// SFSafariViewController — a different browser CONTEXT with its own storage. The
// PKCE code verifier was written by Safari and does not exist there, so the
// `?code=` exchange silently failed and he landed signed-out on the home page.
// Every provider tapping the email on a phone hits this; it is not an edge case.
//
// PKCE cannot survive that hop by design — the verifier is deliberately
// context-bound. So the client moved to the implicit flow, where the link itself
// carries the session in the URL fragment and works in whatever browser opens it.
// (The trade is that the token is in the URL; acceptable here — these are
// short-lived magic links to a low-value console, and the alternative is a flow
// that provably does not work on a phone.)
//
// This module is pure so it can be unit tested: the browser half is one line in
// main.ts. It reads BOTH the fragment and the query, because GoTrue puts the
// session in the fragment but delivers errors in whichever the flow used.

export type AuthLandingKind = 'session' | 'error' | 'none';

export interface AuthLanding {
  kind: AuthLandingKind;
  /** e.g. 'otp_expired' */
  errorCode: string | null;
  /** GoTrue's human sentence, already URL-decoded. */
  errorDescription: string | null;
}

const SESSION_KEYS = ['access_token', 'refresh_token', 'provider_token'];

function parse(part: string): URLSearchParams {
  return new URLSearchParams(part.replace(/^[#?]/, ''));
}

/**
 * Classify a landing from the raw `location.hash` and `location.search`.
 *
 * Order matters: an error wins over a session, because GoTrue can deliver
 * `error=` alongside a stale `type=` and the user must be told rather than
 * silently routed on.
 */
export function readAuthLanding(hash: string, search: string): AuthLanding {
  const fragment = parse(hash ?? '');
  const query = parse(search ?? '');

  const errorCode =
    fragment.get('error_code') ?? query.get('error_code') ??
    fragment.get('error') ?? query.get('error');
  if (errorCode) {
    return {
      kind: 'error',
      errorCode,
      errorDescription:
        fragment.get('error_description') ?? query.get('error_description') ?? null,
    };
  }

  const hasSession =
    SESSION_KEYS.some((k) => fragment.get(k)) ||
    // The implicit flow puts the token in the fragment, but `type=magiclink`
    // (and `?code=` from any leftover PKCE link) also mean "this is a callback,
    // not a normal page load".
    Boolean(fragment.get('type')) ||
    Boolean(query.get('code'));
  if (hasSession) return { kind: 'session', errorCode: null, errorDescription: null };

  return { kind: 'none', errorCode: null, errorDescription: null };
}

/**
 * Is this hash an auth callback rather than one of our `#/route` hashes?
 *
 * main.ts's post-auth router used to early-return unless the path was `/` or
 * `/signin`. An implicit landing arrives as `#access_token=…`, which is neither,
 * so the user was left on whatever the router fell back to. Do NOT assume
 * supabase-js has cleared the fragment by the time this is asked — it does clean
 * the URL after consuming it, but the timing is its business, not ours.
 */
export function isAuthCallbackHash(hash: string): boolean {
  const raw = (hash ?? '').replace(/^#/, '');
  if (raw === '' || raw.startsWith('/')) return false;   // '#/claim' etc.
  return readAuthLanding(hash, '').kind !== 'none';
}

/** A sentence for the provider. GoTrue's own wording is jargon on a phone. */
export function friendlyAuthError(landing: AuthLanding): string {
  switch (landing.errorCode) {
    case 'otp_expired':
      return 'That sign-in link has expired or was already used. Links last 60 minutes and work once.';
    case 'access_denied':
      return 'That sign-in link is no longer valid. It may have expired or already been used.';
    default:
      return landing.errorDescription
        ?? 'We could not sign you in with that link. Please request a fresh one.';
  }
}
